import { type PropertyDto, remindersApi, propertiesApi, type ReminderDto, type UpdateReminderRequest } from '../../services/api';
import { onLocaleChange, t } from '../../services/i18n';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { openReminderCreateDialog } from './reminder-dialogs';
import { type ReminderFilterStatus } from './reminder-shared';
import './rc-reminder-list-view';
import { RcReminderListView } from './rc-reminder-list-view';

const REMINDER_EVENTS = ['add', 'complete', 'delete', 'filter-change'] as const;

export class RcReminderList extends HTMLElement {
  private reminders: ReminderDto[] = [];
  private properties: PropertyDto[] = [];
  private filterStatus: ReminderFilterStatus = 'open';
  private disposeLocaleChange?: () => void;

  async connectedCallback(): Promise<void> {
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    REMINDER_EVENTS.forEach((eventName) => this.addEventListener(`reminder-${eventName}`, this.handleReminderEvent as EventListener));
    await this.loadData();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    REMINDER_EVENTS.forEach((eventName) => this.removeEventListener(`reminder-${eventName}`, this.handleReminderEvent as EventListener));
  }

  private async loadData(): Promise<void> {
    try {
      [this.reminders, this.properties] = await Promise.all([remindersApi.getAll(), propertiesApi.getAll()]);
      this.render();
    } catch (error) {
      console.error('Failed to load reminders:', error);
      this.innerHTML = `<div class="empty-state">${t('reminder.loadError')}</div>`;
    }
  }

  private get filteredReminders(): ReminderDto[] {
    switch (this.filterStatus) {
      case 'open': return this.reminders.filter((reminder) => !reminder.isCompleted);
      case 'completed': return this.reminders.filter((reminder) => reminder.isCompleted);
      default: return this.reminders;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-reminder-list-view></rc-reminder-list-view>';
    const view = this.querySelector('rc-reminder-list-view') as RcReminderListView | null;
    if (!view) return;
    view.reminders = this.reminders;
    view.filteredReminders = this.filteredReminders;
    view.filterStatus = this.filterStatus;
    view.render();
  }

  private handleReminderEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'reminder-add':
        openReminderCreateDialog(this.properties, async () => this.loadData());
        return;
      case 'reminder-complete':
        if (detail.id) await this.handleComplete(detail.id);
        return;
      case 'reminder-delete':
        if (detail.id) await this.handleDelete(detail.id);
        return;
      case 'reminder-filter-change':
        this.filterStatus = detail.filterStatus as ReminderFilterStatus;
        this.render();
        return;
      default:
        return;
    }
  };

  private async handleComplete(id: string): Promise<void> {
    const reminder = this.reminders.find((item) => item.id === id);
    if (!reminder) return;
    try {
      const data: UpdateReminderRequest = {
        title: reminder.title,
        dueDate: reminder.dueDate,
        isCompleted: true,
        recurrenceIntervalValue: reminder.recurrenceIntervalValue,
        recurrenceUnit: reminder.recurrenceUnit,
      };
      await remindersApi.update(id, data);
      await this.loadData();
    } catch (error) {
      console.error('Failed to complete reminder:', error);
    }
  }

  private async handleDelete(id: string): Promise<void> {
    if (!await confirmDialog(t('reminder.confirmDelete'))) return;
    try {
      await remindersApi.delete(id);
      await this.loadData();
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  }
}

if (!customElements.get('rc-reminder-list')) {
  customElements.define('rc-reminder-list', RcReminderList);
}
